import { useTranslation } from 'react-i18next';

import { NgButton, LocationSourceBadgeWithInfo } from '@/ui';
import { isMeasuredLocation } from '@/signalr';
import type { LocationSource, CoordinateLocation } from '@/signalr';

type SourcedLocation = CoordinateLocation & { source: LocationSource };

export type CallerCurrentPositionMarkerDetailTemplateProps = {
  currentPosition: CoordinateLocation;
  positions: readonly CoordinateLocation[];
  onSelect: (locationSource: LocationSource) => void | Promise<void>;
  canSelectSource?: (locationSource: LocationSource) => boolean;
  openManualPositionDialog: () => void | Promise<void>;
  allowSourceSwitching?: boolean;
};

export function CallerCurrentPositionMarkerDetailTemplate({
  currentPosition,
  positions,
  onSelect,
  canSelectSource = () => true,
  openManualPositionDialog,
  allowSourceSwitching = false,
}: CallerCurrentPositionMarkerDetailTemplateProps) {
  const { t } = useTranslation();

  const sortedPositions = positions
    .filter(
      (position): position is SourcedLocation =>
        position.source !== null && position.source !== currentPosition.source,
    )
    .sort((a, b) => Date.parse(b.capturedAtUtc) - Date.parse(a.capturedAtUtc));

  return (
    <div className="d-flex flex-column align-items-start gap-2">
      <h5>{t('Incident_CurrentPositionMarker_Header')}</h5>
      {currentPosition.source !== null && (
        <div>
          <h6>{t('Incident_CurrentPositionMarker_CurrentSource')}</h6>
          <LocationSourceBadgeWithInfo
            source={currentPosition.source}
            timestamp={new Date(currentPosition.capturedAtUtc)}
            accuracy={isMeasuredLocation(currentPosition) ? currentPosition.accuracy : null}
          />
        </div>
      )}
      <div className="d-flex flex-column w-100">
        {sortedPositions.length > 0 && (
          <>
            <h6>{t('Incident_CurrentPositionMarker_AvailableSources')}</h6>
            {sortedPositions.map((item) => (
              <div
                key={item.source}
                className="d-flex flex-row justify-content-between align-items-center gap-2 py-1 border-bottom border-light"
              >
                <LocationSourceBadgeWithInfo
                  source={item.source}
                  timestamp={new Date(item.capturedAtUtc)}
                  accuracy={isMeasuredLocation(item) ? item.accuracy : null}
                />
                {allowSourceSwitching && canSelectSource(item.source) && (
                  <NgButton size="small" onClick={() => onSelect(item.source)}>
                    {t('Incident_CurrentPositionMarker_UseButton')}
                  </NgButton>
                )}
              </div>
            ))}
          </>
        )}
        {allowSourceSwitching && (
          <div className="d-flex flex-row justify-content-center align-items-center gap-2 pt-2">
            <NgButton
              text={
                positions.length > 0
                  ? t('Incident_OpenEditGpsLocationDialog_ChangePosition')
                  : t('Incident_OpenEditGpsLocationDialog_NoPosition')
              }
              icon="fal fa-pen-to-square"
              size="small"
              onClick={openManualPositionDialog}
            ></NgButton>
          </div>
        )}
      </div>
    </div>
  );
}
